import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  at,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  hudRegion,
  notification,
  productScreen,
  panel,
  removePanel,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO 2 — CONNECTED EXPERIENCES
//
// Proactive supply-interruption customer communications. A planned valve
// closure triggers automated multi-channel comms BEFORE customers notice.
//
// UNIQUE VISUAL DNA (vs other scenarios):
// 1. flow-pulse NETWORK — glowing supply lines visible from Act 1, then they
//    DIE when the valve closes (the "invisible work" made visible). They
//    REIGNITE at resolution. No other scenario uses flow-pulse as narrative.
// 2. model-status VALVE — the valve station shifts colour through the story
//    (green → amber → red closure → green restored).
// 3. Persistent multi-site-rag DISTRICTS — three district beacons with
//    constellation lines from Act 2 onward, shifting RAG as comms land.
// 4. SIMULTANEOUS multi-colour comms — SMS/email/portal fire at the same
//    time as overlapping rings (rainbow pulse) in Act 3.
// 5. Purple welfare outbound as the tension device.
//
// No excavation, no pipe-lay, no vehicles. Drama = network death + comms.
// ──────────────────────────────────────────────────────────────────────────

// ─── Sites ─────────────────────────────────────────────────────────────────
const VALVE = infra('gas-governor', gp(24, 4));

// Supply network paths (valve → districts) — these are the flow-pulse lines
const NETWORK_NORTH: [number, number][] = [
  gp(24, 4), gp(24, 2), gp(24, 0), gp(22, -2), gp(20, -2), gp(18, -2),
];
const NETWORK_SOUTH: [number, number][] = [
  gp(24, 4), gp(24, 6), gp(24, 8), gp(22, 10), gp(20, 10), gp(18, 10),
];
const NETWORK_WEST: [number, number][] = [
  gp(24, 4), gp(20, 4), gp(16, 4), gp(14, 4), gp(12, 4),
];

// Residential districts
const DISTRICT_NORTH: [number, number][] = [
  gp(18, -2), gp(20, -2), gp(22, -2), gp(24, -2), gp(26, -2), gp(28, -2),
  gp(18, 0), gp(20, 0), gp(22, 0), gp(24, 0), gp(26, 0), gp(28, 0),
  gp(18, 2), gp(20, 2), gp(22, 2), gp(24, 2), gp(26, 2), gp(28, 2),
];
const DISTRICT_SOUTH: [number, number][] = [
  gp(18, 6), gp(20, 6), gp(22, 6), gp(24, 6), gp(26, 6), gp(28, 6),
  gp(18, 8), gp(20, 8), gp(22, 8), gp(24, 8), gp(26, 8), gp(28, 8),
  gp(18, 10), gp(20, 10), gp(22, 10), gp(24, 10), gp(26, 10), gp(28, 10),
];
const DISTRICT_WEST: [number, number][] = [
  gp(12, 0), gp(14, 0), gp(16, 0),
  gp(12, 2), gp(14, 2), gp(16, 2),
  gp(12, 4), gp(14, 4), gp(16, 4),
  gp(12, 6), gp(14, 6), gp(16, 6),
  gp(12, 8), gp(14, 8), gp(16, 8),
];
const ALL_PROPERTIES: [number, number][] = [
  ...DISTRICT_NORTH, ...DISTRICT_SOUTH, ...DISTRICT_WEST,
];

function highlightAt(coords: [number, number], radius = 120): ScenarioStep {
  return { type: 'highlight-buildings', duration: 0, params: { coordinates: coords, radius } };
}

export const connectedExperiencesScenario = defineScenario(
  {
    id: 'connected-experiences',
    title: 'Proactive Supply Interruption Comms',
    description: 'Supply network dies, multi-channel comms cascade, districts shift RAG, network reignites',
    hudLabel: 'CONNECTME LIVE',
    telemetry: 'connected-experiences',
    activeInfraIds: ['gas-governor'],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // ACT 1: NETWORK ALIVE — Show the supply flowing, then kill it (0–25s)
    // The unique hook: glowing flow-pulse lines show the supply network.
    // Valve goes amber → red. Lines die. The "invisible work" made visible.
    // ═══════════════════════════════════════════════════════════════════════
    act('Network Alive', [
      dimStart,

      // Show the supply network flowing — three glowing arteries
      beat(PACING.ESTABLISH, [
        establishingWide(VALVE, { bearing: -20, pitch: 45, zoom: 14.5 }),
        { type: 'flow-pulse', duration: PACING.ESTABLISH, params: {
          id: 'net-north', path: NETWORK_NORTH, color: '#0ea5e9', speed: 1.2,
        }},
        { type: 'flow-pulse', duration: PACING.ESTABLISH, params: {
          id: 'net-south', path: NETWORK_SOUTH, color: '#0ea5e9', speed: 1.0,
        }},
        { type: 'flow-pulse', duration: PACING.ESTABLISH, params: {
          id: 'net-west', path: NETWORK_WEST, color: '#0ea5e9', speed: 1.1,
        }},
        { type: 'model-status', duration: 0, params: { coordinates: gp(24, 4), infraId: 'gas-governor', color: '#10b981', emissive: 0.4 } },
        hudEvent('programme', 'CONNECTME', 'Supply network active — 342 properties served. Planned maintenance in 4 hours.', 'info'),
        hudRegion([
          { name: 'District North', status: 'supplied', properties: 156 },
          { name: 'District South', status: 'supplied', properties: 124 },
          { name: 'District West', status: 'supplied', properties: 62 },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'supply', kind: 'counter', label: 'Supply Status', value: 100, unit: '%', severity: 'success' } },
      ]),

      // Valve goes AMBER — maintenance window approaching
      beat(PACING.PUSH_IN, [
        dronePushIn(VALVE, { bearing: 10, zoom: 17 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(24, 4), infraId: 'gas-governor', color: '#f59e0b', emissive: 0.6 } },
        hudEvent('control', 'VALVE CLOSURE PENDING', 'Maintenance window T-minus 10 min. Comms sequence must complete before closure.', 'warning'),
        notification('CLOSURE APPROACHING', 'Valve maintenance in 10 minutes. Proactive comms triggered NOW.', 'warning', PACING.BEAT),
      ]),

      // Valve goes RED — supply dies. Flow-pulse lines stop.
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(24, 4), infraId: 'gas-governor', zoom: 17.5, pitch: 65, bearing: 0 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(24, 4), infraId: 'gas-governor', color: '#ef4444', emissive: 0.8 } },
        { type: 'clear-effect', duration: 0, params: { id: 'net-north' } },
        { type: 'clear-effect', duration: 0, params: { id: 'net-south' } },
        { type: 'clear-effect', duration: 0, params: { id: 'net-west' } },
        { type: 'color-grade', duration: 0, params: { tint: '#0ea5e9', intensity: 0.08, vignette: 0.2 } },
        hudEvent('alert', 'VALVE CLOSED', 'Supply interrupted. Network offline. Customers must be informed NOW.', 'critical'),
        { type: 'hud-metric', duration: 0, params: { id: 'supply', kind: 'counter', label: 'Supply Status', value: 0, unit: '%', severity: 'critical' } },
      ]),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 2: DISTRICT RAG + COMMS BEGIN — Beacons appear, first wave (25–60s)
    // Multi-site-rag shows three district beacons (all RED initially).
    // As comms reach each district, beacons shift to GREEN.
    // ═══════════════════════════════════════════════════════════════════════
    act('District Comms', [
      // Districts appear as RED beacons — unaware customers
      beat(PACING.PUSH_IN, [
        craneReveal(VALVE, { zoom: 14.8, bearing: -15 }),
        { type: 'multi-site-rag', duration: 0, params: {
          id: 'districts',
          sites: [
            { coordinates: gp(23, 0), status: 'red' },
            { coordinates: gp(23, 8), status: 'red' },
            { coordinates: gp(14, 4), status: 'red' },
          ],
          linkTo: { coordinates: gp(24, 4), infraId: 'gas-governor' },
        }},
        hudEvent('comms', 'COMMS INITIATED', '3 districts unaware. Multi-channel cascade starting — SMS first.', 'warning'),
        { type: 'hud-metric', duration: 0, params: { id: 'messages', kind: 'counter', label: 'Messages Sent', value: 0, unit: '/ 342', severity: 'warning' } },
        hudTeam([
          { name: 'District North (156)', status: 'active', destination: 'SMS sending' },
          { name: 'District South (124)', status: 'pending', destination: 'Queued' },
          { name: 'District West (62)', status: 'pending', destination: 'Queued' },
        ]),
      ]),

      // SMS hits District North — beacon shifts to AMBER (aware but not confirmed)
      beat(8000, [
        { type: 'camera', duration: 8000, params: { center: gp(23, 0), zoom: 15.5, pitch: 42, bearing: 20 } },
        { type: 'comms-broadcast', duration: 8000, params: {
          infraId: 'gas-governor',
          coordinates: gp(24, 4),
          color: '#10b981',
          radius: 200,
          targets: DISTRICT_NORTH,
          id: 'sms-north',
        }},
        // Undim north district
        highlightAt(gp(18, -2)),
        highlightAt(gp(22, -2)),
        highlightAt(gp(26, -2)),
        highlightAt(gp(18, 0)),
        highlightAt(gp(22, 0)),
        highlightAt(gp(26, 0)),
        highlightAt(gp(20, 2)),
        highlightAt(gp(24, 2)),
        highlightAt(gp(28, 2)),
        { type: 'hud-metric', duration: 0, params: { id: 'messages', kind: 'counter', label: 'Messages Sent', value: 156, unit: '/ 342', severity: 'warning' } },
        notification('DISTRICT NORTH — SMS SENT', '156 customers notified. 48 vulnerable flagged for priority.', 'success', PACING.BEAT),
      ]),

      // Update RAG — north goes amber
      { type: 'clear-effect', duration: 0, params: { id: 'districts' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'districts',
        sites: [
          { coordinates: gp(23, 0), status: 'amber' },
          { coordinates: gp(23, 8), status: 'red' },
          { coordinates: gp(14, 4), status: 'red' },
        ],
        linkTo: { coordinates: gp(24, 4), infraId: 'gas-governor' },
      }} as ScenarioStep,

      // SMS hits District South
      beat(7000, [
        orbit180(at(23, 8), { startBearing: 20, arc: 80, zoom: 15.5, pitch: 42, duration: 7000 }),
        { type: 'comms-broadcast', duration: 7000, params: {
          infraId: 'gas-governor',
          coordinates: gp(24, 4),
          color: '#10b981',
          radius: 280,
          targets: DISTRICT_SOUTH,
          id: 'sms-south',
        }},
        highlightAt(gp(18, 6)),
        highlightAt(gp(22, 6)),
        highlightAt(gp(26, 6)),
        highlightAt(gp(20, 8)),
        highlightAt(gp(24, 8)),
        highlightAt(gp(28, 8)),
        highlightAt(gp(18, 10)),
        highlightAt(gp(22, 10)),
        highlightAt(gp(26, 10)),
        { type: 'hud-metric', duration: 0, params: { id: 'messages', kind: 'counter', label: 'Messages Sent', value: 280, unit: '/ 342', severity: 'warning' } },
        hudTeam([
          { name: 'District North (156)', status: 'complete', destination: 'Delivered' },
          { name: 'District South (124)', status: 'active', destination: 'SMS sending' },
          { name: 'District West (62)', status: 'pending', destination: 'Queued' },
        ]),
      ]),

      // SMS hits District West + update RAG
      { type: 'clear-effect', duration: 0, params: { id: 'sms-north' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'sms-south' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'districts' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'districts',
        sites: [
          { coordinates: gp(23, 0), status: 'amber' },
          { coordinates: gp(23, 8), status: 'amber' },
          { coordinates: gp(14, 4), status: 'red' },
        ],
        linkTo: { coordinates: gp(24, 4), infraId: 'gas-governor' },
      }} as ScenarioStep,

      beat(7000, [
        { type: 'camera', duration: 7000, params: { center: gp(14, 4), zoom: 15.5, pitch: 42, bearing: -20 } },
        { type: 'comms-broadcast', duration: 7000, params: {
          coordinates: gp(16, 4),
          color: '#10b981',
          radius: 200,
          targets: DISTRICT_WEST,
          id: 'sms-west',
        }},
        highlightAt(gp(12, 0)),
        highlightAt(gp(14, 2)),
        highlightAt(gp(16, 2)),
        highlightAt(gp(12, 4)),
        highlightAt(gp(14, 4)),
        highlightAt(gp(16, 4)),
        highlightAt(gp(12, 6)),
        highlightAt(gp(14, 6)),
        highlightAt(gp(16, 8)),
        { type: 'hud-metric', duration: 0, params: { id: 'messages', kind: 'counter', label: 'Messages Sent', value: 342, unit: '/ 342', severity: 'success' } },
        hudEvent('sms', 'ALL SMS DELIVERED', '342 SMS sent across 3 districts. 99.7% delivery rate.', 'success'),
        hudTeam([
          { name: 'District North (156)', status: 'complete', destination: 'Delivered' },
          { name: 'District South (124)', status: 'complete', destination: 'Delivered' },
          { name: 'District West (62)', status: 'complete', destination: 'Delivered' },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'sms-west' } } as ScenarioStep,
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 3: RAINBOW PULSE — All 3 channels simultaneously (60–90s)
    // SMS (green) + Email (blue) + Portal (amber) fire as overlapping rings
    // at the same time. Creates a unique rainbow cascade effect.
    // Districts shift to GREEN as confirmations arrive.
    // ═══════════════════════════════════════════════════════════════════════
    act('Rainbow Pulse', [
      // SIMULTANEOUS triple-channel broadcast — the centrepiece visual
      beat(10000, [
        orbit180(VALVE, { startBearing: -30, arc: 180, zoom: 14.8, pitch: 45, duration: 10000 }),
        // Green (SMS confirmations)
        { type: 'comms-broadcast', duration: 10000, params: {
          infraId: 'gas-governor',
          coordinates: gp(24, 4),
          color: '#10b981',
          radius: 250,
          targets: DISTRICT_NORTH,
          id: 'rainbow-sms',
        }},
        // Blue (email)
        { type: 'comms-broadcast', duration: 10000, params: {
          infraId: 'gas-governor',
          coordinates: gp(24, 4),
          color: '#0ea5e9',
          radius: 320,
          targets: DISTRICT_SOUTH,
          id: 'rainbow-email',
        }},
        // Amber (portal)
        { type: 'comms-broadcast', duration: 10000, params: {
          infraId: 'gas-governor',
          coordinates: gp(24, 4),
          color: '#f59e0b',
          radius: 400,
          targets: DISTRICT_WEST,
          id: 'rainbow-portal',
        }},
        // Broad undimming sweep
        highlightAt(gp(20, -2), 150),
        highlightAt(gp(28, 0), 150),
        highlightAt(gp(20, 4), 150),
        highlightAt(gp(28, 6), 150),
        highlightAt(gp(20, 10), 150),
        highlightAt(gp(12, 4), 150),
        hudEvent('multi', 'MULTI-CHANNEL LIVE', 'SMS + Email + Portal firing simultaneously. Rainbow cascade active.', 'info'),
        notification('3 CHANNELS ACTIVE', 'SMS (green) • Email (blue) • Portal (amber) — all channels broadcasting.', 'info', 5000),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'rainbow-sms' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'rainbow-email' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'rainbow-portal' } } as ScenarioStep,

      // All districts go GREEN
      { type: 'clear-effect', duration: 0, params: { id: 'districts' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'districts',
        sites: [
          { coordinates: gp(23, 0), status: 'green' },
          { coordinates: gp(23, 8), status: 'green' },
          { coordinates: gp(14, 4), status: 'green' },
        ],
        linkTo: { coordinates: gp(24, 4), infraId: 'gas-governor' },
      }} as ScenarioStep,

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(20, 4), zoom: 14.5, pitch: 45, bearing: 0 } },
        hudEvent('confirmed', 'ALL DISTRICTS GREEN', 'All 342 customers across 3 districts confirmed aware. Multi-channel complete.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'ack', kind: 'counter', label: 'Acknowledged', value: 328, unit: '/ 342', severity: 'success' } },
        hudRegion([
          { name: 'District North', status: 'confirmed', properties: 156 },
          { name: 'District South', status: 'confirmed', properties: 124 },
          { name: 'District West', status: 'confirmed', properties: 48 },
        ]),
      ]),

      productScreen('ConnectMe — Multi-Channel Dashboard', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — comms delivery dashboard screenshot TBD',
        role: 'Control Room Operator',
        url: 'app.skewb.io/connectme/dashboard',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 4: WELFARE ESCALATION — West district low response (90–125s)
    // District West drops back to AMBER. Purple outbound welfare calls
    // resolve it. Unique purple wave colour not used anywhere else.
    // ═══════════════════════════════════════════════════════════════════════
    act('Welfare Escalation', [
      // West drops to amber — low acknowledgement
      { type: 'clear-effect', duration: 0, params: { id: 'districts' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'districts',
        sites: [
          { coordinates: gp(23, 0), status: 'green' },
          { coordinates: gp(23, 8), status: 'green' },
          { coordinates: gp(14, 4), status: 'amber' },
        ],
        linkTo: { coordinates: gp(24, 4), infraId: 'gas-governor' },
      }} as ScenarioStep,

      beat(PACING.PUSH_IN, [
        dronePushIn(at(14, 4), { bearing: -10, zoom: 15.8 }),
        hudEvent('alert', 'LOW RESPONSE — WEST', 'District West: 14 vulnerable customers unresponsive. Escalating to welfare outbound.', 'warning'),
        { type: 'hud-metric', duration: 0, params: { id: 'ack', kind: 'counter', label: 'Acknowledged', value: 328, unit: '/ 342', severity: 'warning' } },
        hudRegion([
          { name: 'District North', status: 'confirmed', properties: 156 },
          { name: 'District South', status: 'confirmed', properties: 124 },
          { name: 'District West', status: 'LOW RESPONSE', properties: 62 },
        ]),
      ]),

      // Purple welfare outbound — unique colour
      beat(8000, [
        orbit180(at(14, 4), { startBearing: -10, arc: 120, zoom: 15.8, pitch: 42, duration: 8000 }),
        { type: 'comms-broadcast', duration: 8000, params: {
          coordinates: gp(14, 4),
          color: '#a855f7',
          radius: 180,
          targets: DISTRICT_WEST,
          id: 'welfare-purple',
        }},
        hudEvent('welfare', 'OUTBOUND CALLS', 'Welfare team direct-calling 14 vulnerable customers. Priority line active.', 'warning'),
        notification('WELFARE ESCALATION', '14 vulnerable customers receiving direct phone call. No SMS — human contact.', 'warning', PACING.BEAT),
        hudTeam([
          { name: 'Welfare Call — Batch 1', status: 'active', destination: '7 customers' },
          { name: 'Welfare Call — Batch 2', status: 'active', destination: '7 customers' },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'welfare-purple' } } as ScenarioStep,

      // West resolves — all green
      { type: 'clear-effect', duration: 0, params: { id: 'districts' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'districts',
        sites: [
          { coordinates: gp(23, 0), status: 'green' },
          { coordinates: gp(23, 8), status: 'green' },
          { coordinates: gp(14, 4), status: 'green' },
        ],
        linkTo: { coordinates: gp(24, 4), infraId: 'gas-governor' },
      }} as ScenarioStep,

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(20, 4), zoom: 14.8, pitch: 45, bearing: 0 } },
        { type: 'hud-metric', duration: 0, params: { id: 'ack', kind: 'counter', label: 'Acknowledged', value: 342, unit: '/ 342', severity: 'success' } },
        hudEvent('resolved', 'ALL CONFIRMED', 'All 342 customers confirmed. Zero complaints. Vulnerable welfare complete.', 'success'),
        notification('ALL DISTRICTS GREEN', '100% acknowledgement. Welfare calls successful. Zero escalations.', 'success', PACING.BEAT),
      ]),

      productScreen('Customer Vulnerability — Welfare View', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — vulnerable customer welfare dashboard TBD',
        role: 'Customer Vulnerability Manager',
        url: 'app.skewb.io/connectme/vulnerability',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 5: NETWORK REIGNITES — Valve reopens, flow returns (125–165s)
    // The centrepiece payoff: flow-pulse lines reignite one by one.
    // Valve goes green. Colour grade warms. Final confirmation comms.
    // ═══════════════════════════════════════════════════════════════════════
    act('Network Restored', [
      { type: 'clear-effect', duration: 0, params: { id: 'messages' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'ack' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'supply' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'districts' } } as ScenarioStep,

      // Valve goes GREEN — supply restored
      beat(PACING.PUSH_IN, [
        dronePushIn(VALVE, { bearing: 30, zoom: 17 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(24, 4), infraId: 'gas-governor', color: '#10b981', emissive: 0.8 } },
        hudEvent('complete', 'VALVE REOPENED', 'Supply restored at 12:42 — 18 minutes ahead of window.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'supply', kind: 'counter', label: 'Supply Status', value: 100, unit: '%', severity: 'success' } },
      ]),

      // Flow-pulse lines REIGNITE — the payoff moment
      { type: 'color-grade', duration: 0, params: { tint: '#10b981', intensity: 0.06, vignette: 0.1 } } as ScenarioStep,

      beat(8000, [
        craneReveal(VALVE, { zoom: 14.5, bearing: -20, duration: 8000 }),
        { type: 'flow-pulse', duration: 8000, params: {
          id: 'net-north-live', path: NETWORK_NORTH, color: '#10b981', speed: 1.5,
        }},
        { type: 'flow-pulse', duration: 8000, params: {
          id: 'net-south-live', path: NETWORK_SOUTH, color: '#10b981', speed: 1.3,
        }},
        { type: 'flow-pulse', duration: 8000, params: {
          id: 'net-west-live', path: NETWORK_WEST, color: '#10b981', speed: 1.4,
        }},
        notification('NETWORK REIGNITED', 'Supply flowing to all 3 districts. Network fully restored.', 'success', 5000),
        hudEvent('network', 'FLOW RESTORED', 'All supply lines active. Pressure normalised across 342 properties.', 'success'),
      ]),

      dimEnd,

      // Final confirmation comms — green wave over fully-lit city
      beat(8000, [
        orbit180(VALVE, { startBearing: -20, arc: 160, zoom: 14.8, pitch: 45, duration: 8000 }),
        { type: 'comms-broadcast', duration: 8000, params: {
          infraId: 'gas-governor',
          coordinates: gp(24, 4),
          color: '#10b981',
          radius: 400,
          targets: ALL_PROPERTIES,
          id: 'restore-comms',
        }},
        panel('final-panel', 'Connected Experiences — Complete', 'data', VALVE, [
          { label: 'Properties Notified', value: '342 / 342', status: 'good' },
          { label: 'Delivery Rate', value: '99.7%', status: 'good' },
          { label: 'Complaints', value: '0', status: 'good' },
          { label: 'CSAT Score', value: '9.4 / 10', status: 'good' },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'restore-comms' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'net-north-live' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'net-south-live' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'net-west-live' } } as ScenarioStep,
      removePanel('final-panel'),

      productScreen('Customer Satisfaction — Performance', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — CSAT geospatial performance screenshot TBD',
        role: 'Performance Manager',
        url: 'app.skewb.io/performance/csat',
      }),

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.05, vignette: 0.12 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -25 }),
        notification('CONNECTME DELIVERED', '342 properties — 3 channels — zero complaints — network restored ahead of schedule.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);
